import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'

export type SessionParticipant = {
  name: string
  type: 'group' | 'individual'
  count?: number
  tooltipLines?: string[]
}

function getInitials(name: string): string {
  const trimmed = (name ?? '').trim()
  if (!trimmed) return ''
  const parts = trimmed.split(/\s+/)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  const first = parts[0]
  if (first.length >= 2) return first.slice(0, 2).toUpperCase()
  return first.toUpperCase()
}

type TooltipState = {
  visible: boolean
  index: number
  position: 'top' | 'bottom'
  top: number
  left: number
}

type SessionParticipantsProps = {
  participants: SessionParticipant[]
}

const SessionParticipants: React.FC<SessionParticipantsProps> = ({ participants }) => {
  const individualPalette = ['#367D7D', '#BF4095', '#CC4D33', '#945EBA']
  const groupColor = '#BED0F4'
  const [tooltip, setTooltip] = useState<TooltipState>({ visible: false, index: -1, position: 'top', top: 0, left: 0 })
  const avatarRefs = useRef<(HTMLDivElement | null)[]>([])
  const tooltipRef = useRef<HTMLDivElement | null>(null)

  const pickIndividualColor = (name: string): string => {
    const str = (name ?? '').toString()
    if (!str) return individualPalette[0]
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      hash = (hash * 31 + str.charCodeAt(i)) >>> 0
    }
    const index = hash % individualPalette.length
    return individualPalette[index]
  }

  const showTooltip = (index: number) => {
    const el = avatarRefs.current[index]
    if (!el) return
    const rect = el.getBoundingClientRect()
    const spaceAbove = rect.top
    const pos = spaceAbove < 100 ? 'bottom' : 'top'
    const left = rect.left + rect.width / 2
    const top = pos === 'top' ? rect.top : rect.bottom
    setTooltip({ visible: true, index, position: pos, top, left })
  }

  const hideTooltip = () => {
    setTooltip({ visible: false, index: -1, position: 'top', top: 0, left: 0 })
  }

  // Close tooltip if clicked outside
  useEffect(() => {
    if (!tooltip.visible) return
    const handler = (e: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
        hideTooltip()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [tooltip.visible])

  const visible = participants.slice(0, 5)
  const visibleCount = visible.length

  const activeParticipant = tooltip.visible && tooltip.index >= 0 ? visible[tooltip.index] : null

  return (
    <div className="relative flex items-center gap-0.5">
      <div className="flex -space-x-2">
        {visible.map((p, idx) => {
          const zIndexClass = `z-[${visibleCount - idx}]`
          const bgColor = p.type === 'group' ? groupColor : pickIndividualColor(p.name)
          const initials = getInitials(p.name)
          const showCount = p.type === 'group' && typeof p.count === 'number' && p.count > 0
          const initialsClass = `text-xs font-bold ${p.type === 'group' ? '' : 'text-white'}`.trim()
          const hasTooltip = p.tooltipLines && p.tooltipLines.length > 0

          return (
            <div
              key={idx}
              ref={(el) => { avatarRefs.current[idx] = el }}
              className={`relative ${zIndexClass} flex items-center gap-2`}
              onMouseEnter={() => hasTooltip && showTooltip(idx)}
              onMouseLeave={hideTooltip}
            >
              <div
                className="relative flex size-8 items-center justify-center rounded-full border border-white cursor-default"
                style={{ backgroundColor: bgColor }}
                title={!hasTooltip ? p.name : undefined}
              >
                <span className={initialsClass}>{initials}</span>
                {showCount ? <div className="notification-bottom">{p.count}</div> : null}
              </div>
            </div>
          )
        })}
      </div>
      {participants.length > 5 ? <p>...</p> : null}

      {activeParticipant && activeParticipant.tooltipLines && createPortal(
        <div
          ref={tooltipRef}
          className="pointer-events-none"
          style={{
            position: 'fixed',
            zIndex: 9999,
            left: tooltip.left,
            top: tooltip.position === 'top' ? tooltip.top - 8 : tooltip.top + 8,
            transform: tooltip.position === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
          }}
        >
          <div
            className="min-w-max rounded-lg px-3 py-2 shadow-lg"
            style={{ backgroundColor: '#333333' }}
          >
            {activeParticipant.type === 'group' ? (
              <>
                <p className="text-xs font-semibold text-white mb-1">{activeParticipant.name}</p>
                {activeParticipant.tooltipLines.map((line, i) => (
                  <p key={i} className="text-xs" style={{ color: '#cccccc' }}>{line}</p>
                ))}
              </>
            ) : (
              <p className="text-xs font-semibold text-white">{activeParticipant.name}</p>
            )}
          </div>
          <div
            className="size-0 border-x-[6px] border-x-transparent"
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              ...(tooltip.position === 'top'
                ? { bottom: 0, marginBottom: -6, borderTop: '6px solid #333333' }
                : { top: 0, marginTop: -6, borderBottom: '6px solid #333333' }
              ),
            }}
          />
        </div>,
        document.body
      )}
    </div>
  )
}

export default SessionParticipants
